/**
 * Voluntary Life Cell Specifications
 *
 * LOC-specific cell specs for Voluntary Life benefits.
 * Only contains truly custom cell specs (PopoverList configurations).
 * Standard enum dropdowns use shared cell specs with enum values passed via definitions.
 */
import { RfpVoluntaryLifeSpouseChildBenefitTypeEnum } from "@platform/openapi-types";
import {
  formatChildAgeBandsValue,
  parseChildBands,
} from "@platform/formatting-utils";
import { ITooltipParams, ValueFormatterParams } from "ag-grid-community";

import { type BenefitTypeOption } from "../../components/PlanEditor/components/CellEditors/ChildAgeBandsField";
import { MULTIPLE_DOLLAR_AMOUNTS } from "../../components/PlanEditor/components/CellEditors/childAgeBandValues";
import { createEnumFormatter } from "../../components/PlanEditor/utilities/enumFormatters";
import { CellSpec } from "../shared/cellSpecs";
import { formatPopoverListEntry } from "../shared/cellSpecs/common";

import { spouseChildBenefitDescriptionLabels } from "./customLabels";

interface GuaranteeIssueAgeEntry {
  amount?: number;
  ageLower?: number;
  ageUpper?: number;
}

/** Render format for guarantee issue age-based amounts */
const GUARANTEE_ISSUE_AGE_FORMAT = "$XX,XXX at ages XX-XX";

/** Tooltip message for empty/unconfigured cells */
const TOOLTIP_CLICK_TO_CONFIGURE = "Click to configure";

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

/**
 * Guarantee Issue Age-Based Amounts: `[{amount, ageLower, ageUpper}, ...]`
 * Renders as "$100,000 at ages 18-65, $50,000 at ages 66-70"
 */
export const formatGuaranteeIssueAgesValue = (value: unknown): string => {
  if (!Array.isArray(value) || value.length === 0) {
    return "";
  }

  return (value as GuaranteeIssueAgeEntry[])
    .map((entry) => {
      const amount = entry.amount != null ? formatCurrency(entry.amount) : "";
      const lower = entry.ageLower ?? "";
      const upper = entry.ageUpper ?? "";
      return `${amount} at ages ${lower}-${upper}`;
    })
    .join(", ");
};

/**
 * Guarantee Issue Ages Details (popover list editor)
 * Custom cell for configuring age bands and amounts
 */
export const guaranteeIssueAgesDetails: CellSpec = {
  cellType: "popoverList",
  dataType: "array",
  cellRenderer: "PopoverListRenderer",
  cellEditor: "PopoverListEditor",
  singleClickEdit: true,
  editable: true,
  valueGetter: (params) => {
    const rowData = params.data as Record<string, unknown> | undefined;
    const field = params.colDef.field ?? "";
    const value = rowData?.[field];
    if (typeof value === "string" && value.trim().startsWith("[")) {
      try {
        return JSON.parse(value) as unknown;
      } catch {
        return [];
      }
    }
    return (Array.isArray(value) ? value : []) as unknown[];
  },
  valueFormatter: (params: ValueFormatterParams) =>
    formatGuaranteeIssueAgesValue(params.value),
  valueSetter: (params) => {
    const newValue = Array.isArray(params.newValue) ? params.newValue : [];
    params.data[params.colDef.field || ""] = newValue;
    return true;
  },
  tooltipValueGetter: (params: ITooltipParams) => {
    const value: unknown = params.value;
    if (!Array.isArray(value) || value.length === 0) {
      return TOOLTIP_CLICK_TO_CONFIGURE;
    }
    const fields = [
      { key: "amount" },
      { key: "ageLower" },
      { key: "ageUpper" },
    ];
    const formatted = value
      .map((entry: unknown) =>
        formatPopoverListEntry(
          entry as Record<string, unknown>,
          fields,
          GUARANTEE_ISSUE_AGE_FORMAT,
        ),
      )
      .join(", ");
    return formatted || TOOLTIP_CLICK_TO_CONFIGURE;
  },
  cellEditorParams: {
    title: "Guarantee Issue Age-Based Amounts",
    renderFormat: GUARANTEE_ISSUE_AGE_FORMAT,
    inputFormat: GUARANTEE_ISSUE_AGE_FORMAT,
    fields: [
      {
        key: "amount",
        label: "Amount",
        type: "currency" as const,
        min: 0,
        placeholder: "e.g., 100000",
      },
      {
        key: "ageLower",
        label: "Age Lower",
        type: "number" as const,
        min: 0,
        max: 120,
        placeholder: "e.g., 18",
      },
      {
        key: "ageUpper",
        label: "Age Upper",
        type: "number" as const,
        min: 0,
        max: 120,
        placeholder: "e.g., 65",
      },
    ],
  },
  description: "Configure guarantee issue amounts by age band",
  validation: {
    required: false,
  },
};

// Same 5-option set Present's child age-band editor offers - no sentinel
// (notDisclosed) value, since an analyst never manually selects it here.
const CHILD_AGE_BANDS_TYPE_VALUES = [
  RfpVoluntaryLifeSpouseChildBenefitTypeEnum.increments,
  RfpVoluntaryLifeSpouseChildBenefitTypeEnum.dollarAmount,
  RfpVoluntaryLifeSpouseChildBenefitTypeEnum.percentageOfEmployeeElection,
  MULTIPLE_DOLLAR_AMOUNTS,
  RfpVoluntaryLifeSpouseChildBenefitTypeEnum.notIncluded,
];

const formatChildBenefitType = createEnumFormatter(
  spouseChildBenefitDescriptionLabels,
);

const CHILD_AGE_BANDS_TYPE_OPTIONS: BenefitTypeOption[] =
  CHILD_AGE_BANDS_TYPE_VALUES.map((value) => ({
    value,
    label: formatChildBenefitType(value),
  }));

/**
 * Child Benefit Age Bands: the full `child.benefitAmountsByAge` array
 * (`[{ageBandLower, ageBandUpper, unit, benefitAmount}, ...]`). Present renders
 * this row unconditionally; Build renders it only when the
 * `btx-ch-benefits-field` flag is on (see voluntary-life/definitions.ts and
 * btx-plan-designer's hideProposalOnlyRows), in place of its three fixed
 * child benefit-description fields.
 *
 * On Present the read display and editor still come from the Quotes
 * formatting layer's cellFormatOverride/cellEditorOverride, which take
 * precedence over this spec (see cellEditorSelector in buildColumns.ts). The
 * valueGetter/valueSetter/valueFormatter/cellEditor below are what Build uses.
 */
export const childBenefitAmountsByAge: CellSpec = {
  cellType: "popoverList",
  dataType: "array",
  // No cellRenderer: PopoverListRenderer is generic over a flat
  // {key: value} entry shape (cellEditorParams.fields), driven by a
  // renderFormat DSL - it doesn't understand this field's nested
  // {ageBandLower, ageBandUpper, unit, benefitAmount: {type, amounts}}
  // entries and, given no matching `fields` array, silently renders every
  // entry as an empty string. AG Grid's default renderer already applies
  // valueFormatter below, which is written for this shape.
  cellEditor: "ChildAgeBandsEditor",
  singleClickEdit: true,
  editable: true,
  valueGetter: (params) => {
    const rowData = params.data as Record<string, unknown> | undefined;
    const field = params.colDef.field ?? "";
    return parseChildBands(rowData?.[field]);
  },
  valueFormatter: (params: ValueFormatterParams) =>
    formatChildAgeBandsValue(parseChildBands(params.value)),
  valueSetter: (params) => {
    const newValue = Array.isArray(params.newValue) ? params.newValue : [];
    params.data[params.colDef.field || ""] = newValue;
    return true;
  },
  tooltipValueGetter: (params: ITooltipParams) => {
    const bands = parseChildBands(params.value);
    return bands.length > 0
      ? formatChildAgeBandsValue(bands)
      : TOOLTIP_CLICK_TO_CONFIGURE;
  },
  cellEditorParams: {
    typeOptions: CHILD_AGE_BANDS_TYPE_OPTIONS,
  },
  description: "Configure child benefit amounts by age band",
  validation: {
    required: false,
  },
};
